/**
 * Timezone resolution — single source of truth shared by the React client and
 * the Cloud Functions / MCP backend, so on-screen display, exports, and
 * grouped-report bucketing all agree on "which zone is this viewer in".
 *
 * Zones are always **IANA names** (e.g. `America/New_York`), never fixed UTC
 * offsets — only IANA names survive DST correctly. This matches the convention
 * already used by scheduling (`RecurrenceConfig.timeZone`, `cadence.timezone`).
 *
 * The stored user/org preference may be the sentinel `'auto'` (or empty /
 * absent), meaning "not set — defer to the next fallback". This keeps the
 * feature inert until a user explicitly picks a zone: `'auto'` reproduces the
 * pre-feature browser-local behavior on the client and UTC on the backend.
 */
/** Sentinel meaning "not set — use the next fallback". */
export const AUTO_TIME_ZONE = 'auto';
/** Treat `'auto'` / empty / whitespace as "not set". */
function pick(v) {
    if (!v)
        return undefined;
    const t = v.trim();
    if (!t || t === AUTO_TIME_ZONE)
        return undefined;
    return t;
}
/**
 * Resolve the effective IANA zone: `user → org → browser → 'UTC'`.
 * The result is NOT validated — pass it through {@link safeZone} before handing
 * it to `Intl`/luxon if the source is untrusted.
 */
export function resolveTimeZone(input) {
    return pick(input.userPref) ?? pick(input.orgDefault) ?? pick(input.browserZone) ?? 'UTC';
}
/**
 * Return `zone` if `Intl` accepts it as a valid IANA time zone, else `'UTC'`.
 * Guards every render/export/bucketing call so a garbage stored value can never
 * throw a `RangeError` mid-report.
 */
export function safeZone(zone) {
    if (!zone)
        return 'UTC';
    try {
        // Throws RangeError on an invalid time zone identifier.
        new Intl.DateTimeFormat('en-US', { timeZone: zone });
        return zone;
    }
    catch {
        return 'UTC';
    }
}
/**
 * Short zone label for a "All times in …" annotation, e.g.
 * `America/New_York (GMT-4)`. Falls back to just the zone name if the offset
 * can't be derived. `at` picks the instant whose offset is shown (defaults to
 * now via the caller — pass an explicit Date to keep it deterministic).
 */
export function formatZoneLabel(zone, at) {
    const z = safeZone(zone);
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: z,
            timeZoneName: 'shortOffset',
        }).formatToParts(at);
        const off = parts.find((p) => p.type === 'timeZoneName')?.value;
        return off ? `${z} (${off})` : z;
    }
    catch {
        return z;
    }
}
